import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#0B0F17] text-slate-100">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] relative overflow-hidden border-r border-slate-800 p-12">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1666347485799-d51e1a951822?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#00A896] flex items-center justify-center shadow-lg shadow-[#00A896]/30">
            <Zap className="h-6 w-6 text-[#04141a]" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display font-extrabold text-xl leading-none">SIG MAP</p>
            <p className="text-xs text-slate-400 tracking-wider uppercase mt-1">Remplacement compteurs</p>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
            Centre de pilotage des changement de compteur SIG
          </h1>
          <a
            href="https://slaunay.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 cursor-pointer hover:opacity-80 transition-opacity duration-300"
            data-testid="login-slaunay-link"
          >
            <span className="text-xs text-slate-400 uppercase tracking-wider">Développé par</span>
            <img alt="SLAUNAY" className="h-6 brightness-0 invert" src="/slaunay-logo.webp" />
          </a>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-[#00A896] flex items-center justify-center">
              <Zap className="h-5 w-5 text-[#04141a]" strokeWidth={2.5} />
            </div>
            <p className="font-display font-extrabold text-lg">SIG MAP</p>
          </div>
          <h2 className="font-display text-2xl font-bold">Connexion</h2>
          <p className="text-sm text-slate-400 mt-1 mb-8">Accès responsable planification.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-slate-400">
                Identifiant
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1.5 bg-slate-900/60 border-slate-700 h-11"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-400">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-slate-900/60 border-slate-700 h-11"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-rose-400 mt-4" data-testid="login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full h-11 mt-6 bg-[#00A896] hover:bg-[#028090] text-[#04141a] font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
          </Button>
          <p className="text-xs text-slate-500 mt-6 text-center">
            Accès réservé · SIG MAP
          </p>
        </form>
      </div>
    </div>
  );
}
